import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useTranslation from '../hooks/useTranslation';
import { getConsent, saveConsent } from '../utils/cookieConsent';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h2`
  font-size: ${props => props.theme.fontSize.md};
  font-weight: 600;
  color: ${props => props.theme.color.darkerText};
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.color.lighterText};
  margin-bottom: 32px;
  line-height: 1.6;
`;

const CookieCategory = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: ${props => props.theme.color.secondaryBg};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.color.border};
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CategoryTitle = styled.h4`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 600;
  color: ${props => props.theme.color.darkerText};
  margin: 0;
`;

const CategoryDescription = styled.p`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.color.lighterText};
  margin: 0;
  line-height: 1.6;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${props => props.theme.color.primary};
  }

  &:checked + span:before {
    transform: translateX(24px);
  }

  &:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.theme.color.border};
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SaveButton = styled(Button)`
  background: ${props => props.theme.color.primary};
  color: white;
  flex: 1;

  &:hover:not(:disabled) {
    background: ${props => props.theme.color.primary}dd;
  }
`;

const AcceptAllButton = styled(Button)`
  background: ${props => props.theme.color.secondaryBg};
  color: ${props => props.theme.color.primaryText};
  border: 1px solid ${props => props.theme.color.border};

  &:hover:not(:disabled) {
    background: ${props => props.theme.color.border};
  }
`;

const RejectAllButton = styled(Button)`
  background: transparent;
  color: ${props => props.theme.color.lighterText};
  border: 1px solid ${props => props.theme.color.border};

  &:hover:not(:disabled) {
    background: ${props => props.theme.color.border};
  }
`;

const SuccessMessage = styled.div`
  padding: 16px;
  background: ${props => props.theme.color.primary}22;
  border: 1px solid ${props => props.theme.color.primary};
  border-radius: 6px;
  color: ${props => props.theme.color.primary};
  margin-top: 20px;
  text-align: center;
  font-size: ${props => props.theme.fontSize.sm};
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CookieSettings = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Load existing preferences
    const consent = getConsent();
    if (consent) {
      setPreferences({
        necessary: true, // Always true
        analytics: consent.analytics || false,
        marketing: consent.marketing || false,
        preferences: consent.preferences || false,
      });
    }
  }, []);

  const togglePreference = (key) => {
    if (key !== 'necessary') {
      setPreferences(prev => ({
        ...prev,
        [key]: !prev[key],
      }));
      setHasChanges(true);
      setShowSuccess(false);
    }
  };

  const handleSave = () => {
    saveConsent(preferences);
    setShowSuccess(true);
    setHasChanges(false);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
    setShowSuccess(true);
    setHasChanges(false);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setPreferences(onlyNecessary);
    saveConsent(onlyNecessary);
    setShowSuccess(true);
    setHasChanges(false);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <Container>
      <Title>{t('cookie.customizeTitle', 'Personnaliser les cookies')}</Title>
      <Description>
        {t('cookie.settingsDescription', 'Gérez vos préférences de cookies. Les cookies nécessaires sont toujours activés pour assurer le bon fonctionnement du site.')}
      </Description>

      <CookieCategory>
        <CategoryHeader>
          <CategoryTitle>{t('cookie.necessary', 'Cookies nécessaires')}</CategoryTitle>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={preferences.necessary}
              disabled
            />
            <ToggleSlider />
          </Toggle>
        </CategoryHeader>
        <CategoryDescription>
          {t('cookie.necessaryDesc', 'Ces cookies sont essentiels au fonctionnement du site et ne peuvent pas être désactivés. Ils sont généralement définis en réponse à des actions que vous effectuez et qui constituent une demande de services (connexion, formulaires, etc.).')}
        </CategoryDescription>
      </CookieCategory>

      <CookieCategory>
        <CategoryHeader>
          <CategoryTitle>{t('cookie.analytics', 'Cookies analytiques')}</CategoryTitle>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={preferences.analytics}
              onChange={() => togglePreference('analytics')}
            />
            <ToggleSlider />
          </Toggle>
        </CategoryHeader>
        <CategoryDescription>
          {t('cookie.analyticsDesc', 'Ces cookies nous permettent de compter les visites et les sources de trafic afin de mesurer et d\'améliorer les performances de notre site.')}
        </CategoryDescription>
      </CookieCategory>

      <CookieCategory>
        <CategoryHeader>
          <CategoryTitle>{t('cookie.marketing', 'Cookies marketing')}</CategoryTitle>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={preferences.marketing}
              onChange={() => togglePreference('marketing')}
            />
            <ToggleSlider />
          </Toggle>
        </CategoryHeader>
        <CategoryDescription>
          {t('cookie.marketingDesc', 'Ces cookies peuvent être définis par nos partenaires publicitaires pour créer un profil de vos intérêts et vous montrer des publicités pertinentes sur d\'autres sites.')}
        </CategoryDescription>
      </CookieCategory>

      <CookieCategory>
        <CategoryHeader>
          <CategoryTitle>{t('cookie.preferences', 'Cookies de préférences')}</CategoryTitle>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={preferences.preferences}
              onChange={() => togglePreference('preferences')}
            />
            <ToggleSlider />
          </Toggle>
        </CategoryHeader>
        <CategoryDescription>
          {t('cookie.preferencesDesc', 'Ces cookies permettent au site de se souvenir de vos choix (comme votre nom d\'utilisateur, votre langue ou la région où vous vous trouvez) et de fournir des fonctionnalités améliorées et plus personnelles.')}
        </CategoryDescription>
      </CookieCategory>

      <ButtonGroup>
        <SaveButton onClick={handleSave} disabled={!hasChanges}>
          {t('cookie.savePreferences', 'Enregistrer mes préférences')}
        </SaveButton>
        <AcceptAllButton onClick={handleAcceptAll}>
          {t('cookie.acceptAll', 'Tout accepter')}
        </AcceptAllButton>
        <RejectAllButton onClick={handleRejectAll}>
          {t('cookie.rejectAll', 'Refuser tout')}
        </RejectAllButton>
      </ButtonGroup>

      {showSuccess && (
        <SuccessMessage>
          ✅ {t('cookie.preferencesSaved', 'Vos préférences ont été enregistrées avec succès')}
        </SuccessMessage>
      )}
    </Container>
  );
};

export default CookieSettings;

